import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Toast } from "primereact/toast";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import LandingDropdown from "../../components/LandingDropdown/LandingDropdown";
import LandingNav from "../../components/LandingNav/LandingNav";
import LandingFooter from "../../components/LandingFooter/LandingFooter";
import LandingCopyright from "../../components/LandingCopyright/LandingCopyright";
import SEO from "../../../../components/SEO/SEO";
import N8nChatbot from "../../../../components/N8nChatbot/N8nChatbot";
import localeText from "../../../../locale";
import "../../landing.css";
import SellerPhoto from "../../../images/landing/newphotos/seller-foto.jpg";

const getBenefits = () => [
  {
    icon: "pi pi-box",
    title: localeText("lp2SellerBenefit1Title"),
    description: localeText("lp2SellerBenefit1Desc"),
  },
  {
    icon: "pi pi-shopping-bag",
    title: localeText("lp2SellerBenefit2Title"),
    description: localeText("lp2SellerBenefit2Desc"),
  },
  {
    icon: "pi pi-chart-line",
    title: localeText("lp2SellerBenefit3Title"),
    description: localeText("lp2SellerBenefit3Desc"),
  },
];

const getStats = () => [
  { label: localeText("lp2SellerStat1Label"), value: localeText("lp2SellerStat1Value") },
  { label: localeText("lp2SellerStat2Label"), value: localeText("lp2SellerStat2Value") },
  { label: localeText("lp2SellerStat3Label"), value: localeText("lp2SellerStat3Value") },
];

const getFaqs = () => [
  { question: localeText("lp2SellerFaq1Q"), answer: localeText("lp2SellerFaq1A") },
  { question: localeText("lp2SellerFaq2Q"), answer: localeText("lp2SellerFaq2A") },
  { question: localeText("lp2SellerFaq3Q"), answer: localeText("lp2SellerFaq3A") },
];

const FaqItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="bg-white border-round-lg w-full">
    <button
      onClick={onToggle}
      className="w-full flex justify-content-between align-items-center gap-3 p-4 border-none bg-transparent cursor-pointer text-left"
      style={{ minHeight: "56px" }}
    >
      <span className="font-bold" style={{ color: "var(--extra-ink)" }}>
        {question}
      </span>
      <i
        className={isOpen ? "pi pi-minus" : "pi pi-plus"}
        style={{ color: "var(--extra-green)", fontSize: "0.9rem", flexShrink: 0 }}
      ></i>
    </button>
    {isOpen && (
      <div className="px-4 pb-4">
        <p className="m-0 text-sm line-height-3" style={{ color: "var(--extra-body)" }}>
          {answer}
        </p>
      </div>
    )}
  </div>
);

const SellerProfile = () => {
  const toast = useRef(null);
  const [openFaq, setOpenFaq] = useState(0);
  const benefits = getBenefits();
  const stats = getStats();
  const faqs = getFaqs();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div>
      <SEO
        title="Seller | Extraconomy"
        description={localeText("lp2SellerSeoDescription")}
      />
      <Toast ref={toast} />

      <div
        style={{ backgroundColor: "#3E8B5C", margin: "0" }}
        className="flex align-items-center justify-content-between nav-responsive sticky top-0 z-3"
      >
        <LandingNav scrollToSection={scrollToSection} />
        <Link
          className="hidden xl:flex font-bold gap-3 xl:px-6 px-4 border-noround border-none p-button-no-outline p-button-cart-hover align-items-center text-white"
          style={{ backgroundColor: "#2A2560", fontSize: "16px", height: "100%" }}
          to="/shop"
        >
          <i style={{ fontSize: "24px" }} className="pi pi-shopping-cart"></i>{" "}
          <span className="hidden xl:inline font-system">{localeText("toTheStore")}</span>
        </Link>
        <div className="xl:hidden flex h-full">
          <LandingDropdown scrollToSection={scrollToSection} />
        </div>
      </div>

      <div className="bg-white content-grid landing">
        {/* Hero */}
        <section className="full-width py-6 md:py-8" style={{ backgroundColor: "var(--extra-soft)" }}>
          <div className="flex flex-wrap align-items-center gap-2 text-sm">
            <Link to="/" className="font-semibold no-underline" style={{ color: "var(--extra-green)" }}>
              {localeText("home")}
            </Link>
            <i className="pi pi-chevron-right text-xs" style={{ color: "var(--extra-body)" }}></i>
            <span style={{ color: "var(--extra-body)" }}>{localeText("lp2SellerBadge")}</span>
          </div>

          <div className="grid align-items-center mt-5">
            <div className="col-12 lg:col-6">
              <div className="flex flex-wrap align-items-center gap-3">
                <p className="eyebrow-text m-0">{localeText("lp2SellerBadge")}</p>
                <span
                  className="inline-flex border-round-xl font-bold text-sm px-3 py-2"
                  style={{ backgroundColor: "rgba(62,139,92,0.1)", color: "var(--extra-green)" }}
                >
                  {localeText("lp2SellerPill")}
                </span>
              </div>
              <h1
                className="mt-3 font-bold title"
                style={{
                  maxWidth: "13ch",
                  fontSize: "clamp(40px, 5vw, 66px)",
                  lineHeight: 1.06,
                  letterSpacing: "-0.03em",
                  color: "var(--extra-green)",
                }}
              >
                {localeText("lp2SellerH1")}
              </h1>
              <p
                className="mt-4 text-lg line-height-3"
                style={{ maxWidth: "52ch", color: "var(--extra-body)" }}
              >
                {localeText("lp2SellerHeroDesc")}
              </p>
              <div className="flex flex-column sm:flex-row gap-3 mt-5">
                <Link
                  to="/seller"
                  className="btn-yellow-cta border-round-md px-5 py-3 no-underline inline-flex align-items-center gap-2 justify-content-center"
                >
                  {localeText("lp2SellerCta1")} <i className="pi pi-arrow-right text-sm"></i>
                </Link>
                <Link
                  to="/#plans"
                  className="border-round-md px-5 py-3 font-bold no-underline text-center landing-card-hover"
                  style={{
                    border: "2px solid var(--extra-green)",
                    backgroundColor: "#fff",
                    color: "var(--extra-green)",
                  }}
                >
                  {localeText("lp2SellerCta2")}
                </Link>
              </div>
            </div>
            <div className="col-12 lg:col-6 mt-5 lg:mt-0">
              <div
                className="relative border-round-xl overflow-hidden"
                style={{ backgroundColor: "var(--extra-green-dark)", boxShadow: "0 20px 44px rgba(30,40,30,.12)" }}
              >
                <div
                  aria-hidden="true"
                  className="absolute border-circle"
                  style={{
                    width: "280px",
                    height: "280px",
                    right: "-80px",
                    top: "-80px",
                    border: "1px solid rgba(255,255,255,0.15)",
                  }}
                ></div>
                <div
                  aria-hidden="true"
                  className="absolute border-circle"
                  style={{
                    width: "280px",
                    height: "280px",
                    left: "-64px",
                    bottom: "-96px",
                    backgroundColor: "rgba(62,139,92,0.45)",
                  }}
                ></div>
                <img
                  src={SellerPhoto}
                  alt=""
                  className="relative w-full"
                  style={{ aspectRatio: "4 / 3", objectFit: "cover", zIndex: 1, display: "block" }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* O que você recebe */}
        <section className="full-width bg-white py-6 md:py-8" id="beneficios">
          <div style={{ maxWidth: "720px" }}>
            <p className="eyebrow-text">{localeText("lp2SellerBenefitsEyebrow")}</p>
            <h2
              className="mt-3 font-bold title text-3xl sm:text-4xl"
              style={{ color: "var(--extra-green)", letterSpacing: "-0.02em" }}
            >
              {localeText("lp2SellerBenefitsTitle")}
            </h2>
            <p className="mt-3 text-lg line-height-3" style={{ color: "var(--extra-body)" }}>
              {localeText("lp2SellerBenefitsDesc")}
            </p>
          </div>

          <div className="grid mt-5 py-2">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="col-12 md:col-6 lg:col-4">
                <div
                  className="landing-card-hover h-full p-4 border-round-lg"
                  style={{ backgroundColor: "#fff", border: "1px solid var(--extra-line)" }}
                >
                  <div
                    className="flex align-items-center justify-content-center border-round-lg"
                    style={{
                      width: "48px",
                      height: "48px",
                      backgroundColor: "rgba(62,139,92,0.1)",
                      color: "var(--extra-green)",
                    }}
                  >
                    <i className={benefit.icon} style={{ fontSize: "1.3rem" }}></i>
                  </div>
                  <div className="font-bold text-xl title mt-3" style={{ color: "var(--extra-green)" }}>
                    {benefit.title}
                  </div>
                  <div className="text-sm mt-2" style={{ color: "var(--extra-body)" }}>
                    {benefit.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Estrutura comercial */}
        <section className="full-width py-6 md:py-8" style={{ backgroundColor: "var(--extra-soft)" }}>
          <div className="grid">
            {stats.map((stat) => (
              <div key={stat.label} className="col-12 md:col-4">
                <div
                  className="h-full p-4 border-round-lg"
                  style={{ backgroundColor: "#fff", border: "1px solid var(--extra-line)" }}
                >
                  <p className="m-0 text-sm" style={{ color: "var(--extra-body)" }}>
                    {stat.label}
                  </p>
                  <p className="mt-2 mb-0 font-bold title text-2xl" style={{ color: "var(--extra-green)" }}>
                    {stat.value}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm line-height-3" style={{ color: "var(--extra-body)" }}>
            {localeText("lp2SellerStatsDisclaimer")}
          </p>
        </section>

        {/* FAQ */}
        <section className="full-width py-6 md:py-8" style={{ backgroundColor: "var(--extra-faq)" }}>
          <div className="mx-auto text-center" style={{ maxWidth: "640px" }}>
            <p className="eyebrow-text">{localeText("lp2SellerFaqEyebrow")}</p>
            <h2
              className="mt-3 font-bold title text-3xl sm:text-4xl"
              style={{ color: "var(--extra-green)", letterSpacing: "-0.02em" }}
            >
              {localeText("lp2SellerFaqTitle")}
            </h2>
            <p className="mt-3 text-lg line-height-3" style={{ color: "var(--extra-body)" }}>
              {localeText("lp2SellerFaqDesc")}
            </p>
          </div>
          <div className="flex flex-column gap-3 mt-5 mx-auto" style={{ width: "100%", maxWidth: "800px" }}>
            {faqs.map((faq, index) => (
              <FaqItem
                key={faq.question}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFaq === index}
                onToggle={() => setOpenFaq(openFaq === index ? -1 : index)}
              />
            ))}
          </div>
        </section>

        {/* CTA final */}
        <section className="full-width py-5" style={{ backgroundColor: "var(--extra-green)" }}>
          <div className="flex flex-column lg:flex-row align-items-start lg:align-items-center justify-content-between gap-4">
            <div>
              <h2 className="font-bold title text-3xl sm:text-4xl text-white m-0">
                {localeText("lp2SellerCtaTitle")}
              </h2>
              <p className="mt-2 m-0" style={{ color: "rgba(255,255,255,0.75)", maxWidth: "48ch" }}>
                {localeText("lp2SellerCtaDesc")}
              </p>
            </div>
            <Link
              to="/seller"
              className="inline-flex align-items-center gap-2 border-round-md px-5 py-3 font-bold no-underline flex-shrink-0"
              style={{ backgroundColor: "#fff", color: "var(--extra-green)" }}
            >
              {localeText("lp2SellerCtaButton")} <i className="pi pi-arrow-right text-sm"></i>
            </Link>
          </div>
        </section>

        <LandingFooter />
        <LandingCopyright />
        <section
          className="py-4 full-width"
          style={{ backgroundColor: "#FFFFFF", color: "#5B6472", borderTop: "1px solid #DDE6E0" }}
        >
          <div className="flex flex-wrap align-items-center justify-content-between gap-3">
            <div
              className="flex flex-wrap align-items-center gap-2 text-xs"
              style={{ color: "#5B6472" }}
            >
              <span>EXTRACONOMY GLOBAL srl · Via Nomentana, 1018 - 00137 Roma (IT)</span>
              <span className="hidden md:inline">|</span>
              <span>P. Iva e C.F. 16948681008 | REA 1686351</span>
            </div>
            <Link to="/privacy" className="text-xs" style={{ color: "#5B6472" }}>
              Privacy Policy
            </Link>
          </div>
        </section>
      </div>

      <N8nChatbot isConfimeaCardVisible={false} />
    </div>
  );
};

export default SellerProfile;
